import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, TrendingUp, TrendingDown, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Arabic month names — indexed 1..12 so the array is padded with an empty
// slot at [0]. `monthKey` is "YYYY-MM"; we split and read index [1..12].
const AR_MONTHS = [
  "", "كانون الثاني", "شباط", "آذار", "نيسان", "أيار", "حزيران",
  "تموز", "آب", "أيلول", "تشرين الأول", "تشرين الثاني", "كانون الأول",
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const MonthlyNetProfitModal = ({ open, onClose }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/monthly-net-profit?year=${year}`);
        if (!cancelled) setMonths(data.months || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, year]);

  // Build a 12-slot grid so every month of the year has a visible circle
  // (empty months show 0). This mirrors the "دوائر الأشهر" UX from the
  // manager expenses screen — the admin sees the whole year at a glance.
  const grid = useMemo(() => {
    const byKey = Object.fromEntries(months.map((m) => [m.month, m]));
    return Array.from({ length: 12 }, (_, i) => {
      const idx = i + 1;
      const key = `${year}-${String(idx).padStart(2, "0")}`;
      const rec = byKey[key];
      return {
        month: key,
        idx,
        name: AR_MONTHS[idx],
        profit: rec?.profit || 0,
        expenses: rec?.expenses || 0,
        net: rec?.net || 0,
        cartons: rec?.cartons || 0,
        revenue: rec?.revenue || 0,
      };
    });
  }, [months, year]);

  const yearNet = grid.reduce((s, m) => s + m.net, 0);
  const yearProfit = grid.reduce((s, m) => s + m.profit, 0);
  const yearExpenses = grid.reduce((s, m) => s + m.expenses, 0);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/70 flex items-center justify-center p-3"
      dir="rtl"
      data-testid="monthly-net-profit-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="text-white p-5 sticky top-0" style={{ background: "linear-gradient(135deg, #1E3A5F, #3B82F6)" }}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-black text-lg">صافي الأرباح والمصاريف الشهرية</h3>
                <p className="text-[11px] opacity-80">دائرة لكل شهر — يتصفّر بداية كل شهر جديد تلقائياً</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="monthly-net-profit-close"
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Year selector + year total */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1 bg-white/15 rounded-full px-1 py-1">
              <button
                type="button"
                onClick={() => setYear((y) => y - 1)}
                data-testid="monthly-year-prev"
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="min-w-[80px] text-center font-black text-lg" dir="ltr">{year}</div>
              <button
                type="button"
                onClick={() => setYear((y) => y + 1)}
                data-testid="monthly-year-next"
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"
                disabled={year >= new Date().getFullYear()}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
            <div className="text-right">
              <p className="text-[10px] opacity-80">صافي السنة</p>
              <p className="font-black text-2xl" dir="ltr" style={{ color: yearNet >= 0 ? "#86efac" : "#fca5a5" }}>
                {fmt(yearNet)} د.ع
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-white/10 rounded-xl p-2">
              <p className="text-[10px] opacity-80 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> الأرباح</p>
              <p className="font-black text-base" dir="ltr">{fmt(yearProfit)}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2">
              <p className="text-[10px] opacity-80 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> المصاريف</p>
              <p className="font-black text-base" dir="ltr">{fmt(yearExpenses)}</p>
            </div>
          </div>
        </div>

        {/* Body — 12 month circles */}
        <div className="p-5">
          {loading ? (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-full bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
              {grid.map((m) => {
                const positive = m.net >= 0;
                const isEmpty = m.profit === 0 && m.expenses === 0;
                const gradient = isEmpty
                  ? "from-slate-200 to-slate-300"
                  : positive
                  ? "from-emerald-500 to-emerald-700"
                  : "from-rose-500 to-rose-700";
                return (
                  <motion.button
                    key={m.month}
                    type="button"
                    onClick={() => setSelected(m)}
                    data-testid={`monthly-circle-${m.month}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className={`aspect-square rounded-full bg-gradient-to-br ${gradient} text-white shadow-lg flex flex-col items-center justify-center p-2 relative overflow-hidden`}
                  >
                    <span className="text-[11px] font-bold opacity-95">{m.name}</span>
                    <span className="text-[10px] opacity-75 mb-1" dir="ltr">{m.month}</span>
                    <span className="font-black text-lg leading-none" dir="ltr">
                      {isEmpty ? "—" : fmt(Math.round(m.net))}
                    </span>
                    {!isEmpty && (
                      <span className="text-[9px] opacity-90 mt-0.5">صافي (د.ع)</span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail popup for a tapped month */}
      <AnimatePresence>
        {selected && (
          <div
            className="fixed inset-0 z-[131] bg-black/70 flex items-center justify-center p-3"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden"
              data-testid="monthly-detail"
            >
              <div className="text-white p-4" style={{ background: "linear-gradient(135deg, #1E3A5F, #3B82F6)" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <h4 className="font-black text-base">{selected.name} — {selected.month}</h4>
                </div>
              </div>
              <div className="p-4 space-y-2">
                <Row label="إجمالي الأرباح" value={selected.profit} color="text-emerald-700" />
                <Row label="إجمالي المصاريف" value={selected.expenses} color="text-rose-700" />
                <div className="border-t pt-2 mt-2">
                  <Row label="صافي الشهر" value={selected.net} color={selected.net >= 0 ? "text-emerald-800" : "text-rose-800"} bold />
                </div>
                <div className="border-t pt-2 mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-slate-500">إجمالي الإيراد</p>
                    <p className="font-bold text-slate-800" dir="ltr">{fmt(selected.revenue)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">عدد الكراتين</p>
                    <p className="font-bold text-slate-800" dir="ltr">{fmt(selected.cartons)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  data-testid="monthly-detail-close"
                  className="w-full mt-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 rounded-full text-sm"
                >
                  إغلاق
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Row = ({ label, value, color, bold }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-600">{label}</span>
    <span className={`${color} ${bold ? "font-black text-lg" : "font-bold"}`} dir="ltr">{fmt(Math.round(value))} د.ع</span>
  </div>
);

export default MonthlyNetProfitModal;
